import React, { useState } from 'react';

import './offer-card.css';
import { Button } from '@wwe-portal/ui/components/button/button';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { OfferCardDetails } from './offer-cards-details/offer-cards-details';
import { cn } from '@wwe-portal/ui/lib/lib';
import { OfferCardHoverState } from './hover-state/hover-state';
import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { getDailyOfferAvailableDays } from '@wwe-portal/web/utils/offer/helper';
import { NOT_READY } from '@wwe-portal/web/utils/constants/offer';
import {
  parseRegionalOfferPrice,
  parseRegionalOfferStrikethroughPrice,
} from '@wwe-portal/web/utils/offer/mapper';

function OfferCard({
  offer,
  onHandlePurchase,
  transactionStarted,
  hasWebCoinPrice = false,
  offerType,
  index,
  handleClickBanner,
}: Readonly<{
  offer: OfferData;
  onHandlePurchase: (offer: OfferData, offerType: string, e: any) => void;
  handleClickBanner?: (offer: OfferData, offerType: string, e: any) => void;
  transactionStarted: string | undefined | number;
  hasWebCoinPrice: boolean;
  ruby?: boolean;
  activeSelectItem?: boolean;
  offerType: string;
  index: number;
}>) {
  const [show, setShow] = useState(false);

  const availability = getDailyOfferAvailableDays(offer.schedules);

  const price = parseRegionalOfferPrice(offer);
  const strikeThroughPrice = parseRegionalOfferStrikethroughPrice(offer);
  const bonusPercentage =
    offer.BonusPercentage && offer.BonusPercentage > 0
      ? offer.BonusPercentage
      : undefined;

  const dataOffers =
    Array.isArray(offer.OfferItemImage) && offer.OfferItemImage.length
      ? offer.OfferItemImage.filter(
          (_, index) => !offer?.OfferItemTexts?.[index]?.includes('MORE')
        )
      : [];

  /** InitialPrice field has been deprecated, use Price instead */
  const getPrice = () => {
    return offer.Price &&
      offer.defaultCurrency &&
      offer.Price?.[offer.defaultCurrency]
      ? offer.Price?.[offer.defaultCurrency]
      : 0;
  };

  return (
    <div className="shop-offer-card relative h-full overflow-hidden rounded-lg bg-[#272b4a]">
      <OfferCardHoverState
        index={index}
        offerId={offer.OffersID}
        transactionStarted={transactionStarted}
        offerContent={offer.offerItems?.map(({ itemName }) => itemName)}
        offerDescription={offer.StoreItemDescription}
        onHandlePurchase={(e) => onHandlePurchase(offer, offerType, e)}
        price={getPrice()}
        initialPrice={getPrice()}
        stockQuantity={offer.StockQuantity}
        currency={offer.defaultCurrency}
        show={show}
        setShow={setShow}
        offerType={offerType}
        isActiveDay={offer.PortalAvailability}
        isInFuture={offer.Availability === NOT_READY}
        isWebCoinCard={true}
        hasWebCoinPrice={hasWebCoinPrice}
        webCoinPrice={offer.AlternativeCostAmount}
      />
      <div
        className={cn('', {
          'info-container-blurred': show,
        })}
      >
        <ClickableDiv
          className={`shop-offer-card__images relative flex w-full ${
            offer.OfferItemImage && offer.OfferItemImage.length > 2
              ? 'justify-center'
              : ''
          }`}
          style={{
            backgroundImage: `url(${offer.OfferBackgroundImage})`,
            backgroundSize: offerType === 'daily' ? 'cover' : '120%',
            backgroundPosition: 'center',
            padding: '10px 0',
            backgroundRepeat: 'no-repeat',
          }}
          onClick={(e) => handleClickBanner?.(offer, offerType, e)}
        >
          {bonusPercentage ? (
            <div className="badge">
              <img src="/assets/icons/ShopBadgeDesktop.svg" alt="" />
              <p className="badge-text">{Math.floor(bonusPercentage)}%</p>
            </div>
          ) : null}
          {offer.StrikeThroughPrice && offer.StrikeThroughPrice > 0 ? (
            <Button
              variant="secondary"
              className={`h-[30px]strike-btn mx-0 flex items-center justify-center p-0`}
            >
              <p className="mb-0 ml-[1px] px-[10px] py-0 text-xs font-semibold underline">
                <s>{strikeThroughPrice.price}</s>
              </p>
            </Button>
          ) : null}
          <div
            className={`relative flex items-center justify-center ${
              offer.OfferItemImage && offer.OfferItemImage.length > 2
                ? `w-full flex-wrap`
                : 'w-full'
            }`}
          >
            {offer.OfferItemImage ? (
              Array.isArray(offer.OfferItemImage) &&
              offer.OfferItemImage.length ? (
                dataOffers.map((image, index) => (
                  <div key={index} className="relative flex flex-col">
                    <img
                      style={{
                        maxHeight:
                          (offer?.OfferItemImage?.length ?? 0) > 2
                            ? 70
                            : offer?.OfferItemImage?.length === 2
                              ? 100
                              : 130,
                        marginTop: 0,
                      }}
                      src={image}
                      className="img-fluid p-[5px]"
                      alt="feature_card_image"
                    />
                    {!strikeThroughPrice ? (
                      <p className="item-quantity mb-0 text-center font-bold">
                        {offer.OfferItemTexts && offer.OfferItemTexts.length > 0
                          ? offer.OfferItemTexts[index]
                          : null}
                      </p>
                    ) : null}
                  </div>
                ))
              ) : (
                <img
                  src={offer.OfferItemImage as any}
                  className="img-fluid"
                  alt="feature_card_image"
                />
              )
            ) : null}
          </div>
        </ClickableDiv>
        <div className="shop-offer-card__content w-full p-[15px]">
          <OfferCardDetails
            offerId={offer.OffersID}
            transactionStarted={transactionStarted}
            endDate={offer.EndDate || ''}
            quantity={offer.StockQuantity}
            onHandlePurchase={(e) => onHandlePurchase(offer, offerType, e)}
            name={
              (offerType === 'rubies'
                ? offer?.OfferItemContent?.[0]
                : offer.StoreItemName) || ''
            }
            price={price}
            availability={offerType === 'daily' ? availability : ''}
            setShow={setShow}
            offerType={offerType}
            isActiveDay={offer.PortalAvailability}
            isInFuture={offer.Availability === NOT_READY}
          />
        </div>
      </div>
    </div>
  );
}

export { OfferCard };
